import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetNotification } from '../../../context/Notification';
import envVars from '../../../constants/envVars';
import useRequest from '../../../hooks/useRequest';
import { LEAD_DETAILS } from './constants';

// HWM-22633 — dr-chrono booking hook for the medical-check slot picker
// (`ScheduleMedicalCheckModal`). Endpoint contract is the archived dr-chrono
// spec at `orchestrator/specs/archive/2026-04-07-drchrono-em-visit-booking.md`.
//
// HWM-23070: the backend takes the slot's `start_utc` as `scheduled_time_utc`
// and converts to clinic wall-clock server-side. `scheduled_time` remains the
// naive-clinic-local alternative; sending both is a 400, hence the union.

export type ScheduleMedicalCheckPayload = { lead_id: string } & (
    | { scheduled_time_utc: string; scheduled_time?: never }
    | { scheduled_time: string; scheduled_time_utc?: never }
);

// [round 4d] `isReschedule` flips the success toast to "rescheduled"
// so the reschedule chain (cancel + book) surfaces a single,
// truthful notification. Default false preserves the original toast
// for the fresh-booking flow.
const useScheduleMedicalCheck = (
    onSuccess?: () => void,
    { isReschedule = false }: { isReschedule?: boolean } = {}
) => {
    const req = useRequest();
    const queryClient = useQueryClient();
    const setNotification = useSetNotification();

    const request = ({ payload }: { payload: ScheduleMedicalCheckPayload }) => {
        return req({
            url: `${envVars.API_RTM_HOSTNAME}scheduling/book-appointment/`,
            method: 'POST',
            body: JSON.stringify({ ...payload, source: 'enterprise_portal' })
        });
    };

    return useMutation({
        mutationFn: request,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [LEAD_DETAILS] });
            setNotification({
                message: isReschedule ? 'Medical check rescheduled' : 'Medical check scheduled'
            });
            onSuccess && onSuccess();
        },
        onError: (error: { errors: string[] }) => {
            return error;
        }
    });
};

export default useScheduleMedicalCheck;
